import { useEffect, useState } from "react";
import classNames from "classnames";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import styles from "./style.module.scss";
import {
  CreateBlogService,
  GetAllBlogsService,
  UpdateBlogService,
  DeleteBlogService,
} from "shared/services/blogs";
import { Icons } from "assets";
import CustomToolTip from "shared/components/common/customToolTip";
import ConfirmationModal from "shared/modal/confirmation";
import BlogFormModal from "shared/modal/blogForm";
import { Blog, BlogFormData } from "shared/types/blog";
import { handleErrors } from "shared/utils/helper";
import CustomButton from "shared/components/common/customButton";

interface BlogResponse {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  image: {
    type: string;
    fullpath: string;
  };
}

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [blogs, setBlogs] = useState<BlogResponse[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedBlog, setSelectedBlog] = useState<BlogResponse | undefined>();
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Thumbnail", key: "thumbnail" },
    { title: "Title", key: "title" },
    { title: "Content", key: "content" },
    { title: "Created At", key: "createdAt" },
    { title: "Action", key: "action" },
  ];

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllBlogsService(page);
      const { data, meta } = response.data.data;
      setBlogs(data || []);
      setTotalItems(meta?.totalItems || 0);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toastMessage("error", "Failed to fetch blogs.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = async (formData: BlogFormData) => {
    try {
      setLoading(true);
      const response = await CreateBlogService(formData);
      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Blog created successfully");
        await fetchBlogs(currentPage);
        setShowFormModal(false);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBlog = async (formData: BlogFormData) => {
    if (!selectedBlog) return;

    try {
      setLoading(true);
      const response = await UpdateBlogService(
        selectedBlog.id.toString(),
        formData
      );
      if (response.status === 200) {
        toastMessage("success", "Blog updated successfully");
        await fetchBlogs(currentPage);
        setShowFormModal(false);
        setSelectedBlog(undefined);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!selectedBlog) return;

    try {
      setDeleteLoading(true);
      const response = await DeleteBlogService(selectedBlog.id.toString());
      if (response.status === 200) {
        toastMessage("success", "Blog deleted successfully");
        await fetchBlogs(currentPage);
        setShowDeleteModal(false);
        setSelectedBlog(undefined);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openCreateForm = () => {
    setSelectedBlog(undefined);
    setShowFormModal(true);
  };

  const openEditForm = (blog: BlogResponse) => {
    setSelectedBlog(blog);
    setShowFormModal(true);
  };

  const openDeleteModal = (blog: BlogResponse) => {
    setSelectedBlog(blog);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchBlogs(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>Blogs</h2>
          <CustomButton
            title="Add Blog"
            onClick={openCreateForm}
            containerStyle={styles.addButton}
          />
        </div>
        <CustomTable
          heads={tableHeads}
          title="Blogs"
          isEmpty={!loading && !blogs.length}
        >
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : blogs.map((blog) => (
                <tr key={blog.id}>
                  <td>
                    <img
                      src={blog.image?.fullpath}
                      alt={blog.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                  </td>
                  <td>{blog.title}</td>
                  <td className="max-w-xs truncate">{blog.content}</td>
                  <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <div
                          onClick={() => {
                            window.open(
                              `https://isopowersportsparts.com/general/blogs/${blog.id}`,
                              "_blank"
                            );
                          }}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Eye />
                        </div>
                      </CustomToolTip>
                      <CustomToolTip label="Edit">
                        <div
                          onClick={() => openEditForm(blog)}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Pencil />
                        </div>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <div
                          onClick={() => openDeleteModal(blog)}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Cross />
                        </div>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
        <CustomPagination
          pageSize={10}
          currentPage={currentPage}
          totalCount={totalItems}
          onPageChange={handlePageChange}
        />
      </div>

      <BlogFormModal
        show={showFormModal}
        handleClose={() => {
          setShowFormModal(false);
          setSelectedBlog(undefined);
        }}
        onSubmit={selectedBlog ? handleUpdateBlog : handleCreateBlog}
        initialData={selectedBlog}
        loading={loading}
      />

      <ConfirmationModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setSelectedBlog(undefined);
        }}
        handleSubmit={handleDeleteBlog}
        loading={deleteLoading}
        title="Delete Blog"
        subtitle="Are you sure you want to delete this blog? This action cannot be undone."
        actionButtonText="Delete"
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    <td>
      <SkeletonLoader className={styles.lineLoader} />
    </td>
    <td>
      <SkeletonLoader className={styles.lineLoader} />
    </td>
    <td>
      <SkeletonLoader className={styles.lineLoader} />
    </td>
    <td>
      <SkeletonLoader className={styles.lineLoader} />
    </td>
    <td>
      <SkeletonLoader className={styles.lineLoader} />
    </td>
  </tr>
);

export default Blogs;
